import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useTranslator } from '@/hooks/useTranslator';
import { NewCompanyMergeForm } from '../../-components/NewCompanyMergeForm';
import { useNewCompanyMerge } from '../../-hooks/useNewCompanyMerge';

const newCompanyMergeSearch = z.object({
  /** The duplicate — a company page links here with its own id. */
  source: z.string().optional(),
  /** The company that remains. */
  target: z.string().optional(),
});

export const Route = createFileRoute('/management/company-merges/(pages)/new/')(
  {
    validateSearch: newCompanyMergeSearch,
    component: NewCompanyMergePage,
  },
);

function NewCompanyMergePage() {
  const { source, target } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });

  useBreadcrumbLabel('new', t('mergesPage.new.breadcrumb'));

  const merge = useNewCompanyMerge({
    sourceId: source ?? null,
    targetId: target ?? null,
    onSourceChange: id =>
      void navigate({
        search: prev => ({ ...prev, source: id ?? undefined }),
        replace: true,
      }),
    onTargetChange: id =>
      void navigate({
        search: prev => ({ ...prev, target: id ?? undefined }),
        replace: true,
      }),
    onQueued: mergeId =>
      void navigate({
        to: '/management/company-merges/$mergeId',
        params: { mergeId },
      }),
  });

  return (
    <PageContainer>
      <NewCompanyMergeForm
        merge={merge}
        onCancel={() =>
          void navigate({
            to: '/management/company-merges',
            search: { page: 1, limit: 50 },
          })
        }
      />
    </PageContainer>
  );
}
